import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Clock, Warning, Package, Truck, CheckCircle, DownloadSimple, ArrowRight, Info, MagnifyingGlass, X, Tag, Copy, Archive, ChatCircle, ImageSquare, CurrencyCircleDollar as CurrencyCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  received: { label: "Received", color: "bg-zinc-100 text-zinc-700 border-zinc-200", icon: Clock },
  quoted: { label: "Quoted", color: "bg-cyan-50 text-cyan-700 border-cyan-200", icon: CurrencyCircle },
  approved: { label: "Approved", color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle },
  info_needed: { label: "Info Needed", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Warning },
  compounding: { label: "Compounding", color: "bg-blue-50 text-blue-700 border-blue-200", icon: Package },
  shipped: { label: "Shipped", color: "bg-violet-50 text-violet-700 border-violet-200", icon: Truck },
  delivered: { label: "Delivered", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle },
};

const NEXT_STATUS = {
  received: "quoted",
  quoted: "approved",
  approved: "compounding",
  info_needed: "quoted",
  compounding: "shipped",
  shipped: "delivered",
};

const NEXT_LABEL = {
  received: "Quoted",
  quoted: "Approved",
  approved: "Compounding",
  info_needed: "Quoted",
  compounding: "Shipped",
  shipped: "Delivered",
};

const NEXT_COLOR = {
  received: "bg-cyan-600 hover:bg-cyan-700",
  quoted: "bg-green-600 hover:bg-green-700",
  approved: "bg-blue-600 hover:bg-blue-700",
  info_needed: "bg-cyan-600 hover:bg-cyan-700",
  compounding: "bg-violet-600 hover:bg-violet-700",
  shipped: "bg-emerald-600 hover:bg-emerald-700",
};

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [patientFilter, setPatientFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  // Batch selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Tracking dialog (only for shipped)
  const [trackingDialog, setTrackingDialog] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");

  // Quote/Price dialog (simple price entry)
  const [quoteDialog, setQuoteDialog] = useState(null);
  const [quoteTotalPrice, setQuoteTotalPrice] = useState("");
  const [quoteNotes, setQuoteNotes] = useState("");

  // Script image viewer
  const [viewingImage, setViewingImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const viewScript = async (fileIds) => {
    if (!fileIds || fileIds.length === 0) { toast.error("No script attached"); return; }
    setImageLoading(true);
    setViewingImage("loading");
    try {
      const res = await axios.get(`${API}/files/${fileIds[0]}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setViewingImage(url);
    } catch { toast.error("Failed to load script"); setViewingImage(null); }
    setImageLoading(false);
  };

  // Archive dialog
  const [archiveDialog, setArchiveDialog] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");

  // Info Needed dialog (with message)
  const [infoDialog, setInfoDialog] = useState(null);
  const [infoMessage, setInfoMessage] = useState("");

  // Tag dialog
  const [tagDialog, setTagDialog] = useState(null);
  const [tagInput, setTagInput] = useState("");

  const TAG_OPTIONS = ["VIP", "Recurring", "Urgent Review", "Awaiting Script", "Follow Up"];

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20, sort: "desc" });
    if (statusFilter === "action_required") {
      params.append("status", "quoted");
      params.append("status2", "info_needed");
    } else if (statusFilter !== "all") {
      params.append("status", statusFilter);
    }
    if (search) params.append("search", search);
    if (patientFilter) params.append("patient", patientFilter);
    if (tagFilter) params.append("tag", tagFilter);
    if (showArchived) params.append("archived", "true");
    else params.append("archived", "false");
    if (dateFrom) {
      const [y, m, d] = dateFrom.split("-").map(Number);
      params.append("date_from_utc", new Date(y, m - 1, d, 0, 0, 0).toISOString());
    }
    if (dateTo) {
      const [y, m, d] = dateTo.split("-").map(Number);
      params.append("date_to_utc", new Date(y, m - 1, d, 23, 59, 59).toISOString());
    }
    try {
      const { data } = await axios.get(`${API}/orders?${params}`, { withCredentials: true });
      setOrders(data.orders);
      setTotalPages(data.pages);
      setTotalCount(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, statusFilter, search, dateFrom, dateTo, patientFilter, tagFilter, showArchived]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Batch toggle
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === orders.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(orders.map(o => o.id)));
  };
  const batchUpdate = async (status) => {
    if (selectedIds.size === 0) return;
    try {
      const { data } = await axios.put(`${API}/orders/batch-status`, { order_ids: Array.from(selectedIds), status }, { withCredentials: true });
      toast.success(`${data.updated} orders updated to ${status}`);
      setSelectedIds(new Set());
      fetchOrders();
    } catch { toast.error("Batch update failed"); }
  };

  // Advance order to next status (one-click for most, dialog for shipped)
  const advanceStatus = async (order) => {
    const nextStatus = NEXT_STATUS[order.status];
    if (!nextStatus) return;

    // If advancing to "shipped", open dialog for tracking number
    if (nextStatus === "shipped") {
      setTrackingDialog(order);
      setTrackingNumber(order.tracking_number || "");
      return;
    }

    try {
      await axios.put(`${API}/orders/${order.id}/status`, {
        status: nextStatus, note: "", tracking_number: ""
      }, { withCredentials: true });
      toast.success(`Status updated to ${NEXT_LABEL[order.status]}`);
      fetchOrders();
    } catch { toast.error("Failed to update status"); }
  };

  // Submit shipped status with tracking number
  const handleShipOrder = async () => {
    if (!trackingDialog) return;
    try {
      await axios.put(`${API}/orders/${trackingDialog.id}/status`, {
        status: "shipped", note: "", tracking_number: trackingNumber
      }, { withCredentials: true });
      toast.success("Order marked as Shipped");
      setTrackingDialog(null);
      fetchOrders();
    } catch { toast.error("Failed to update status"); }
  };

  // Set order to info_needed with message
  const handleRequestInfo = async () => {
    if (!infoDialog || !infoMessage.trim()) { toast.error("Please describe what info is needed"); return; }
    try {
      // Send message first
      await axios.post(`${API}/orders/${infoDialog.id}/messages`, { message: infoMessage }, { withCredentials: true });
      // Then set status
      await axios.put(`${API}/orders/${infoDialog.id}/status`, {
        status: "info_needed", note: infoMessage, tracking_number: ""
      }, { withCredentials: true });
      toast.success("Info requested - message sent to customer");
      setInfoDialog(null); setInfoMessage("");
      fetchOrders();
    } catch { toast.error("Failed"); }
  };

  const openQuoteDialog = (order) => {
    setQuoteDialog(order);
    // Pre-fill with existing price if already quoted
    const existingTotal = order.total - (order.delivery_fee || 0);
    setQuoteTotalPrice(existingTotal > 0 ? existingTotal.toFixed(2) : "");
    setQuoteNotes("");
  };

  const handleQuoteSubmit = async () => {
    if (!quoteDialog || !quoteTotalPrice) { toast.error("Enter a price"); return; }
    const price = parseFloat(quoteTotalPrice);
    try {
      await axios.put(`${API}/orders/${quoteDialog.id}/add-items`, {
        items: [{ product_id: "", type: "Custom", ingredient: "Compounded Medication (as per script)", strength: "", qty: "", price, quote_required: false }]
      }, { withCredentials: true });
      // If there's a note, send as message too
      if (quoteNotes.trim()) {
        await axios.post(`${API}/orders/${quoteDialog.id}/messages`, { message: quoteNotes }, { withCredentials: true });
      }
      toast.success("Quote sent to customer");
      setQuoteDialog(null);
      fetchOrders();
    } catch { toast.error("Failed to send quote"); }
  };

  // Archive
  const handleArchive = async () => {
    if (!archiveDialog || !archiveReason.trim()) { toast.error("Reason required for audit"); return; }
    try {
      await axios.put(`${API}/orders/${archiveDialog.id}/archive`, { reason: archiveReason }, { withCredentials: true });
      toast.success("Order archived");
      setArchiveDialog(null); setArchiveReason("");
      fetchOrders();
    } catch { toast.error("Failed"); }
  };
  const handleUnarchive = async (order) => {
    try { await axios.put(`${API}/orders/${order.id}/unarchive`, {}, { withCredentials: true }); toast.success("Restored"); fetchOrders(); } catch { toast.error("Failed"); }
  };

  // Duplicate
  const handleDuplicate = async (order) => {
    try { await axios.post(`${API}/orders/${order.id}/duplicate`, {}, { withCredentials: true }); toast.success("Order duplicated"); fetchOrders(); } catch { toast.error("Failed"); }
  };

  // Tags
  const openTagDialog = (order) => { setTagDialog(order); setTagInput(""); };
  const addTag = async (tag) => {
    if (!tagDialog) return;
    const tags = [...(tagDialog.tags || [])];
    if (!tags.includes(tag)) tags.push(tag);
    try { await axios.put(`${API}/orders/${tagDialog.id}/tags`, { tags }, { withCredentials: true }); toast.success("Tag added"); setTagDialog({ ...tagDialog, tags }); fetchOrders(); } catch { toast.error("Failed"); }
  };
  const removeTag = async (tag) => {
    if (!tagDialog) return;
    const tags = (tagDialog.tags || []).filter(t => t !== tag);
    try { await axios.put(`${API}/orders/${tagDialog.id}/tags`, { tags }, { withCredentials: true }); setTagDialog({ ...tagDialog, tags }); fetchOrders(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-orders-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">ADMIN</p>
        <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Order Management</h1>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="order-filters">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Search by store, name, email, order ID..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 rounded-none text-sm"
            data-testid="order-search-input"
          />
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-44 rounded-none text-sm" data-testid="status-filter-select">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="action_required">Action Required</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="compounding">Compounding</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="border border-zinc-300 px-2 py-1.5 text-sm font-mono w-36"
              data-testid="date-from-input"
            />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="border border-zinc-300 px-2 py-1.5 text-sm font-mono w-36"
              data-testid="date-to-input"
            />
          </div>
          {(search || dateFrom || dateTo || statusFilter !== "all") && (
            <button
              onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setStatusFilter("all"); setPage(1); }}
              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
              title="Clear all filters"
              data-testid="clear-filters"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-mono text-zinc-400" data-testid="results-count">
          {totalCount} order{totalCount !== 1 ? "s" : ""} found (newest first)
        </p>
        <div className="flex items-center gap-3">
          {patientFilter && (
            <button onClick={() => setPatientFilter("")} className="text-xs text-[#0055FF] hover:underline">Clear patient: "{patientFilter}"</button>
          )}
          {tagFilter && (
            <button onClick={() => setTagFilter("")} className="text-xs text-[#0055FF] hover:underline">Clear tag: "{tagFilter}"</button>
          )}
          <button
            onClick={() => { setShowArchived(!showArchived); setPage(1); }}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${showArchived ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50"}`}
            data-testid="toggle-archived"
          >
            {showArchived ? "Viewing Archive" : "Show Archive"}
          </button>
        </div>
      </div>

      {/* Batch Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-zinc-950 text-white" data-testid="batch-action-bar">
          <span className="text-sm font-mono">{selectedIds.size} selected</span>
          <button onClick={() => batchUpdate("compounding")} className="px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 transition-colors" data-testid="batch-compounding">Compounding</button>
          <button onClick={() => batchUpdate("shipped")} className="px-3 py-1.5 text-xs font-semibold bg-violet-600 hover:bg-violet-700 transition-colors" data-testid="batch-shipped">Shipped</button>
          <button onClick={() => batchUpdate("delivered")} className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 transition-colors" data-testid="batch-delivered">Delivered</button>
          <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1.5 text-xs font-medium bg-zinc-700 hover:bg-zinc-600 transition-colors ml-auto" data-testid="batch-clear">Clear</button>
        </div>
      )}

      {/* Orders Table */}
      <div className="border border-zinc-200 overflow-x-auto" data-testid="admin-orders-table">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="border-b-2 border-zinc-950 py-3 px-2 w-10">
                <Checkbox checked={selectedIds.size === orders.length && orders.length > 0} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
              </th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Order</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">From</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Items</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Total</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Status</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Date</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-12 text-center text-sm text-zinc-500 font-mono">LOADING...</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={8} className="py-12 text-center text-sm text-zinc-500">No orders found</td></tr>
            ) : orders.map(order => {
              const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.received;
              const StatusIcon = sc.icon;
              const nextStatus = NEXT_STATUS[order.status];
              const nextLabel = NEXT_LABEL[order.status];
              const nextColor = NEXT_COLOR[order.status];
              const isDelivered = order.status === "delivered";
              const isInfoNeeded = order.status === "info_needed";

              return (
                <tr key={order.id} className="border-b border-zinc-200 hover:bg-zinc-50 transition-colors" data-testid={`admin-order-row-${order.id}`}>
                  <td className="py-3 px-2">
                    <Checkbox checked={selectedIds.has(order.id)} onCheckedChange={() => toggleSelect(order.id)} data-testid={`select-order-${order.id}`} />
                  </td>
                  <td className="py-3 px-4 font-mono text-xs text-zinc-500">
                    #{order.id?.slice(0, 8)}
                    {order.is_quote_request && <span className="block text-[10px] text-amber-600 font-bold">QUOTE REQ</span>}
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm text-zinc-800 font-medium">{order.user_name}</p>
                    <p className="text-xs text-zinc-400">{order.store_name || order.user_role?.replace("_", " ")}</p>
                  </td>
                  <td className="py-3 px-4 text-sm text-zinc-600">
                    <div className="flex items-center gap-1.5">
                      <span>{order.items?.length} item{order.items?.length !== 1 ? "s" : ""}</span>
                      {order.has_quote_items && <span className="text-amber-600 text-xs font-mono">*QUOTE</span>}
                      {order.prescription_file_ids?.length > 0 && (
                        <button onClick={() => viewScript(order.prescription_file_ids)} className="p-1 text-violet-500 hover:text-violet-700 hover:bg-violet-50 transition-colors" title="View Script" data-testid={`view-script-${order.id}`}>
                          <ImageSquare size={16} weight="duotone" />
                        </button>
                      )}
                    </div>
                    {order.patient_name && <p className="text-xs text-zinc-400 truncate max-w-[120px]">{order.patient_name}</p>}
                  </td>
                  <td className="py-3 px-4 text-sm font-mono font-semibold text-zinc-950">${order.total?.toFixed(2)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono font-medium border ${sc.color}`}>
                        <StatusIcon size={12} /> {sc.label}
                      </span>
                      {(order.tags || []).map((tag, ti) => (
                        <span key={ti} className="px-1.5 py-0.5 text-[10px] font-mono bg-blue-50 text-blue-700 border border-blue-200 cursor-pointer" onClick={() => setTagFilter(tag)}>{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-xs text-zinc-400">{new Date(order.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {/* Main action: advance to next status */}
                      {nextStatus && (
                        <button
                          onClick={() => advanceStatus(order)}
                          className={`px-3 py-1.5 text-xs font-semibold text-white transition-colors flex items-center gap-1 ${nextColor}`}
                          data-testid={`advance-status-${order.id}`}
                        >
                          {nextLabel} <ArrowRight size={12} />
                        </button>
                      )}
                      {isDelivered && (
                        <span className="px-3 py-1.5 text-xs font-mono font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Complete
                        </span>
                      )}

                      {/* Quote / Edit Quote button */}
                      {(order.has_quote_items || order.is_quote_request || order.status === "quoted") && (
                        <button
                          onClick={() => openQuoteDialog(order)}
                          className={`px-3 py-1.5 text-xs font-medium text-white transition-colors ${
                            order.status === "quoted" || (order.items?.length > 0 && !order.items.some(i => i.quote_required))
                              ? "bg-cyan-600 hover:bg-cyan-700" : "bg-amber-500 hover:bg-amber-600"
                          }`}
                          data-testid={`quote-price-${order.id}`}
                        >{order.status === "quoted" || (order.items?.length > 0 && !order.items.some(i => i.quote_required)) ? "Edit Quote" : "Quote"}</button>
                      )}

                      {/* Request Info button (opens message dialog) */}
                      {!isInfoNeeded && !isDelivered && (
                        <button
                          onClick={() => { setInfoDialog(order); setInfoMessage(""); }}
                          className="px-2 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors flex items-center gap-1"
                          data-testid={`request-info-${order.id}`}
                          title="Request more information"
                        >
                          <Info size={12} /> Info
                        </button>
                      )}

                      {/* Invoice download */}
                      <button
                        onClick={() => {
                          axios.get(`${API}/orders/${order.id}/invoice`, { withCredentials: true, responseType: "blob" })
                            .then(res => {
                              const url = URL.createObjectURL(res.data);
                              const a = document.createElement("a");
                              a.href = url; a.download = `invoice-${order.id.slice(0,8)}.pdf`; a.click();
                              URL.revokeObjectURL(url);
                            }).catch(() => toast.error("Failed to download"));
                        }}
                        className="px-2 py-1.5 text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200 transition-colors"
                        data-testid={`invoice-${order.id}`}
                        title="Download Invoice"
                      >
                        <DownloadSimple size={14} />
                      </button>
                      {/* Tags */}
                      <button onClick={() => openTagDialog(order)} className="px-2 py-1.5 text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200 transition-colors" title="Tags" data-testid={`tags-${order.id}`}>
                        <Tag size={14} />
                      </button>
                      {/* Duplicate */}
                      <button onClick={() => handleDuplicate(order)} className="px-2 py-1.5 text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200 transition-colors" title="Duplicate Order" data-testid={`duplicate-${order.id}`}>
                        <Copy size={14} />
                      </button>
                      {/* Archive */}
                      {!order.archived ? (
                        <button onClick={() => { setArchiveDialog(order); setArchiveReason(""); }} className="px-2 py-1.5 text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200 transition-colors" title="Archive" data-testid={`archive-${order.id}`}>
                          <Archive size={14} />
                        </button>
                      ) : (
                        <button onClick={() => handleUnarchive(order)} className="px-2 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors" title="Restore" data-testid={`unarchive-${order.id}`}>
                          <ArrowRight size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-zinc-500">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 text-sm border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50">Previous</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 text-sm border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {/* Tracking Number Dialog (only when advancing to Shipped) */}
      <Dialog open={!!trackingDialog} onOpenChange={(open) => { if (!open) setTrackingDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="tracking-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Mark as Shipped</DialogTitle>
          </DialogHeader>
          {trackingDialog && (
            <div className="space-y-4">
              <div className="bg-zinc-50 p-3 border border-zinc-200">
                <p className="text-xs font-mono text-zinc-500">Order #{trackingDialog.id?.slice(0, 8)}</p>
                <p className="text-sm font-medium text-zinc-950">{trackingDialog.user_name} - {trackingDialog.items?.length} items</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Tracking Number</Label>
                <Input
                  value={trackingNumber}
                  onChange={e => setTrackingNumber(e.target.value)}
                  className="mt-1.5 rounded-none"
                  data-testid="tracking-number-input"
                  placeholder="Enter Australia Post tracking number"
                />
              </div>
              <button
                onClick={handleShipOrder}
                className="w-full bg-violet-600 text-white font-semibold py-3 hover:bg-violet-700 transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
                data-testid="confirm-ship-button"
              >
                <Truck size={18} /> MARK AS SHIPPED
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Simple Quote Dialog */}
      <Dialog open={!!quoteDialog} onOpenChange={(open) => { if (!open) setQuoteDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{quoteDialog?.status === "quoted" ? "Edit Quote" : "Price Quote"}</DialogTitle>
          </DialogHeader>
          {quoteDialog && (
            <div className="space-y-4">
              <div className="bg-amber-50 p-3 border border-amber-200">
                <p className="text-xs font-mono text-amber-600">Order #{quoteDialog.id?.slice(0, 8)}</p>
                <p className="text-sm font-medium text-amber-800">{quoteDialog.user_name} ({quoteDialog.user_email})</p>
                {quoteDialog.patient_name && <p className="text-xs text-amber-600 mt-1">Patient: {quoteDialog.patient_name}</p>}
                {quoteDialog.notes && <p className="text-xs text-amber-600 mt-1">Notes: {quoteDialog.notes}</p>}
                {quoteDialog.prescription_file_ids?.length > 0 && (
                  <button onClick={() => viewScript(quoteDialog.prescription_file_ids)} className="mt-2 px-3 py-1.5 text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors flex items-center gap-1" data-testid="view-script-in-quote">
                    <ImageSquare size={14} /> View Prescription Script
                  </button>
                )}
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Total Price ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={quoteTotalPrice}
                  onChange={e => setQuoteTotalPrice(e.target.value)}
                  className="mt-1.5 rounded-none text-lg font-mono"
                  placeholder="0.00"
                  data-testid="quote-total-price"
                  autoFocus
                />
                {quoteDialog.delivery_fee > 0 && (
                  <p className="text-xs text-zinc-500 mt-1">+ ${quoteDialog.delivery_fee.toFixed(2)} delivery = <span className="font-semibold">${(parseFloat(quoteTotalPrice || 0) + quoteDialog.delivery_fee).toFixed(2)} total</span></p>
                )}
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Note to customer (optional)</Label>
                <Textarea value={quoteNotes} onChange={e => setQuoteNotes(e.target.value)} rows={2} className="mt-1.5 rounded-none" placeholder="e.g. Price valid for 7 days..." data-testid="quote-notes" />
              </div>
              <button
                onClick={handleQuoteSubmit}
                disabled={!quoteTotalPrice}
                className="w-full bg-amber-500 text-white font-semibold py-3 hover:bg-amber-600 transition-colors disabled:opacity-50"
                data-testid="submit-quote"
              >
                {quoteDialog.status === "quoted" ? "UPDATE QUOTE & NOTIFY CUSTOMER" : "SEND QUOTE TO CUSTOMER"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Info Needed Dialog */}
      <Dialog open={!!infoDialog} onOpenChange={(open) => { if (!open) setInfoDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="info-needed-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Request More Information</DialogTitle></DialogHeader>
          {infoDialog && (
            <div className="space-y-4">
              <div className="bg-zinc-50 p-3 border border-zinc-200">
                <p className="text-xs font-mono text-zinc-500">Order #{infoDialog.id?.slice(0, 8)}</p>
                <p className="text-sm font-medium text-zinc-950">{infoDialog.user_name}</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">What information do you need? *</Label>
                <Textarea value={infoMessage} onChange={e => setInfoMessage(e.target.value)} rows={4} className="mt-1.5 rounded-none" placeholder="e.g. Please provide clearer photo of prescription, confirm patient DOB, clarify dosage..." data-testid="info-message-input" />
              </div>
              <p className="text-xs text-zinc-400">This message will be sent to the customer and the order will be marked "Info Needed".</p>
              <button onClick={handleRequestInfo} disabled={!infoMessage.trim()} className="w-full bg-amber-500 text-white font-semibold py-3 hover:bg-amber-600 transition-colors disabled:opacity-50" data-testid="send-info-request">SEND & REQUEST INFO</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={!!archiveDialog} onOpenChange={(open) => { if (!open) setArchiveDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="archive-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Archive Order</DialogTitle></DialogHeader>
          {archiveDialog && (
            <div className="space-y-4">
              <div className="bg-zinc-50 p-3 border border-zinc-200">
                <p className="text-xs font-mono text-zinc-500">Order #{archiveDialog.id?.slice(0, 8)}</p>
                <p className="text-sm font-medium text-zinc-950">{archiveDialog.user_name} - {archiveDialog.items?.length} items</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Reason for Archiving (required for audit) *</Label>
                <Textarea value={archiveReason} onChange={e => setArchiveReason(e.target.value)} rows={3} className="mt-1.5 rounded-none" placeholder="e.g. Duplicate order, cancelled by customer..." data-testid="archive-reason-input" />
              </div>
              <button onClick={handleArchive} disabled={!archiveReason.trim()} className="w-full bg-zinc-950 text-white font-semibold py-3 hover:bg-zinc-800 transition-colors disabled:opacity-50" data-testid="confirm-archive">ARCHIVE ORDER</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog open={!!tagDialog} onOpenChange={(open) => { if (!open) setTagDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="tag-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Manage Tags</DialogTitle></DialogHeader>
          {tagDialog && (
            <div className="space-y-4">
              <p className="text-xs font-mono text-zinc-500">Order #{tagDialog.id?.slice(0, 8)}</p>
              {(tagDialog.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tagDialog.tags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200">{tag}<button onClick={() => removeTag(tag)} className="ml-1 text-blue-400 hover:text-red-500"><X size={10} /></button></span>
                  ))}
                </div>
              )}
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">Quick Add</p>
                <div className="flex flex-wrap gap-2">
                  {TAG_OPTIONS.filter(t => !(tagDialog.tags || []).includes(t)).map(tag => (
                    <button key={tag} onClick={() => addTag(tag)} className="px-3 py-1.5 text-xs font-medium bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200 transition-colors">+ {tag}</button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Custom tag..." className="rounded-none flex-1" data-testid="custom-tag-input" />
                <button onClick={() => { if (tagInput.trim()) { addTag(tagInput.trim()); setTagInput(""); } }} className="bg-[#0055FF] text-white px-4 py-2 text-sm font-semibold hover:bg-[#0044CC]">Add</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Script Image Viewer */}
      <Dialog open={!!viewingImage} onOpenChange={(open) => { if (!open) { if (viewingImage && viewingImage !== "loading") URL.revokeObjectURL(viewingImage); setViewingImage(null); } }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] p-2" data-testid="script-viewer-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo] px-2 pt-2">Prescription Script</DialogTitle></DialogHeader>
          <div className="flex items-center justify-center min-h-[300px]">
            {viewingImage === "loading" ? (
              <p className="font-mono text-sm text-zinc-500">Loading script...</p>
            ) : viewingImage ? (
              <img src={viewingImage} alt="Prescription script" className="max-w-full max-h-[75vh] object-contain" data-testid="script-image" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
